import { MessagesHolder } from '@augloop/types-chat';
import { AddOperation } from '@augloop/types-core';
import { v4 as uuidv4 } from '@augloop/utils';
export class ChatHelper {
    constructor(config) {
        this.config = config;
        this.nextMessageId = 0;
        this.responseCallbacks = new Map();
        const chatsPath = ['session', 'chats'];
        const chatId = uuidv4();
        const messagesHolderId = 'messages';
        const chatPath = [...chatsPath, chatId];
        this.messagesPath = [...chatPath, messagesHolderId];
        // Start chat
        this.config.session.submitOperations([
            new AddOperation({
                parentPath: chatsPath,
                items: [{
                        id: chatId,
                        body: config.chat
                    }]
            }),
            new AddOperation({
                parentPath: chatPath,
                items: [{
                        id: messagesHolderId,
                        body: new MessagesHolder()
                    }]
            })
        ]);
    }
    /**
     * Creates an instance of IChatHelper which can be used to chat/send prompts to Generative AI/LLMs.
     * You can create as many Chat helpers as you'd like.
     * Make sure to call "close" once finished with particular instance in order to release any resources.
     *
     * @remark To use full fledged and advanced options leverage ISession interface. See https://aka.ms/augloop/llm/movetoisession for instructions.
     *
     * @example
     *
     * ChatHelper<TextChatMessage, BasicChatResponse>.create({
     *     session: session,
     *     chat: new BasicChat({
     *         modelConfig: new OpenAiTextModelConfig({
     *             model: OpenAiModel.ChatGpt,
     *             parameters: {
     *                 temperature: 0.9
     *             },
     *             context: 'You are friendly and helpful AI assistant chatting with a human. Your replies should at most be 3 paragraphs.',
     *             prependText: '\n\nHuman:\n',
     *             postpendText: '\n\nYour reply:\n',
     *         })
     *     }),
     *     responseType: BasicChatResponse.getTypeName(),
     * }).then(chatHelper => {
     *     chatHelper.send(new TextChatMessage({
     *         message: 'put user message here'
     *     }),
     *     (response: BasicChatResponse) => {
     *         if (response.status === AnnotationStatus.Success) {
     *             console.log(params.response.text);
     *             console.log(params.response.usedModel);
     *         }
     *         // Note that you should close chat helper when no longer needed. Also note that response callback will not be invoked if no response is received from the server.
     *         // It is recommended to close the helper in a different part of the app, when appropriate. For example, user closing the pane where the chat helper is located,
     *         // assuming new opening of pane starts a new chat instance.
     *         chatHelper.close();
     *     });
     * });
     */
    static create(config) {
        const helper = new ChatHelper(config);
        return helper.activateAnnotation(config.responseType).then(() => helper);
    }
    send(message, responseCallback) {
        const id = `${this.nextMessageId++}`;
        this.responseCallbacks.set(id, responseCallback);
        this.config.session.submitOperations([
            new AddOperation({
                parentPath: this.messagesPath,
                items: [{
                        id,
                        body: message
                    }]
            })
        ]);
    }
    close() {
        return this.config.session.releaseAnnotation(this.activatedAnnotationToken);
    }
    activateAnnotation(annotationType) {
        return this.config.session.activateAnnotation(annotationType, {
            callback: (op) => {
                if (AddOperation.typeGuard(op) && this.isUnderSubtree(op.parentPath, this.messagesPath)) {
                    for (const item of op.items) {
                        const parentId = op.parentPath[op.parentPath.length - 1];
                        const responseCallback = this.responseCallbacks.get(parentId);
                        if (responseCallback) {
                            responseCallback(item.body);
                            this.responseCallbacks.delete(parentId);
                        }
                    }
                }
            }
        }).then(({ token }) => { this.activatedAnnotationToken = token; });
    }
    isUnderSubtree(path, subtree) {
        if (path.length < subtree.length) {
            return false;
        }
        for (let i = 0; i < subtree.length; i++) {
            if (path[i] !== subtree[i]) {
                return false;
            }
        }
        return true;
    }
}
//# sourceMappingURL=chat-helper.js.map